import { isEqual } from 'lodash';
import { useEffect, useRef, useState } from 'react';
import { useClient } from 'urql';

import {
  COMPOSITION_GET_DATA_V2,
  type CompositionV2DataQuery,
  type CompositionV2DataQueryVariables,
  type CompositionV2Filters,
} from '@revelio/data-access';
import { NoData } from '@revelio/replots';

import { useEffectiveFilters } from '../../../filters/hooks/useEffectiveFilters';
import { usePrimaryFilterState } from '../../../filters/hooks/usePrimaryFilterState';
import type { PrimaryFilterId } from '../../../filters/types/filters';
import { getSecondaryPrimaryDimension } from '../../../filters/utils/get-secondary-primary-dimension';
import { getV2Filters } from '../../../filters/utils/getV2Filters';
import { useChartPrimaryFilter } from '../../../hooks/useChartPrimaryFilter';
import { ChartFallback } from '../../Chart/ChartFallback';
import { useChart } from '../../Chart/ChartProvider';
import type { CompositionMetricValues } from '../../PageConfigProvider/types/metric';
import { isSingleEntityChart } from '../../PageState/utils/is-single-entity-chart';
import {
  type SupportedSecondaryDimensions,
  type TopEntity,
  TopNDataProvider,
} from '../TopNDataProvider';
import { type DataProviderChildren, DataProviderQueryStatus } from '../types';
import {
  type CompositionTopNDataSetType,
  convertCompositionTopNData,
} from './convert-composition-top-n-data';

type CompositionTopNQueryProps = {
  primaryDimension: PrimaryFilterId;
  secondaryDimension: SupportedSecondaryDimensions;
  metric: CompositionMetricValues;
  isTimeseries?: boolean;
  exchangeRate?: number;
  children: DataProviderChildren<CompositionTopNDataSetType | undefined>;
};

export const CompositionTopNDataProvider = ({
  primaryDimension,
  secondaryDimension,
  metric,
  isTimeseries,
  exchangeRate,
  children,
}: CompositionTopNQueryProps) => {
  /** Query top n companies, geos, or roles using the compensation query
   * For each company, send off multiple composition queries where the returned roles/geos are the primary entity while filtered to each company
   */
  return (
    <TopNDataProvider
      primaryDimension={primaryDimension}
      secondaryDimension={secondaryDimension}
    >
      {({ data, status }) => {
        if (!data || status === DataProviderQueryStatus.Fetching) {
          return <ChartFallback status={status} />;
        }
        if (data.length === 0) {
          return <NoData />;
        }
        return (
          <CompositionMultiQuery
            primaryDimension={primaryDimension}
            secondaryDimension={secondaryDimension}
            topEntities={data}
            metric={metric}
            isTimeseries={isTimeseries}
            exchangeRate={exchangeRate}
          >
            {({ data, status }) => (
              <ChartFallback status={status}>
                {children({ data, status })}
              </ChartFallback>
            )}
          </CompositionMultiQuery>
        );
      }}
    </TopNDataProvider>
  );
};

const CompositionMultiQuery = ({
  primaryDimension,
  secondaryDimension,
  metric,
  topEntities,
  isTimeseries,
  exchangeRate,
  children,
}: {
  primaryDimension: PrimaryFilterId;
  secondaryDimension: SupportedSecondaryDimensions;
  metric: CompositionMetricValues;
  isTimeseries?: boolean;
  topEntities: TopEntity[];
  exchangeRate?: number;
  children: DataProviderChildren<CompositionTopNDataSetType | undefined>;
}) => {
  const effectiveFilters = useEffectiveFilters({ viewGranularity: 'month' });
  const { primaryFilter } = usePrimaryFilterState(primaryDimension);

  const { item } = useChart();

  const selections = primaryFilter?.selections;
  const primarySelections =
    selections?.map((selection) => ({
      [selection.levelId]: [Number(selection?.id?.split('_')?.slice(-1)?.[0])],
    })) ?? [];

  const chartPrimaryFilter = useChartPrimaryFilter({ primaryDimension });

  const isSingleEntity = isSingleEntityChart(item);
  const filteredPrimarySelections = isSingleEntity
    ? [chartPrimaryFilter]
    : primarySelections;

  const compositionFilters = filteredPrimarySelections.map(
    (primarySelection): CompositionV2DataQueryVariables => ({
      entities: topEntities,
      filters: {
        ...getV2Filters({
          primaryDimension: getSecondaryPrimaryDimension(secondaryDimension),
          isSnapshot: !isTimeseries,
          effectiveFilters,
        }),
        ...(primarySelection as unknown as CompositionV2Filters),
      },
    })
  );

  const gqlClient = useClient();

  const [fetching, setFetching] = useState(false);
  const [hasError, setHasError] = useState(false);
  const [compositionData, setCompositionData] = useState<
    { dim: string; data: CompositionV2DataQuery['composition_v2'] }[]
  >([]);

  const lastCompositionFilterRef = useRef<CompositionV2DataQueryVariables[]>(
    []
  );

  // biome-ignore lint/correctness/useExhaustiveDependencies: intentional dependency list
  useEffect(() => {
    (async () => {
      if (isEqual(lastCompositionFilterRef.current, compositionFilters)) {
        return;
      }

      setFetching(true);
      setHasError(false);

      try {
        const compositionResponses = await Promise.all(
          compositionFilters.map((filter) =>
            gqlClient.query(COMPOSITION_GET_DATA_V2, filter)
          )
        );

        lastCompositionFilterRef.current = compositionFilters;
        setCompositionData(
          compositionResponses.map((response, i) => ({
            data: response.data?.composition_v2,
            dim: selections?.[i]?.name ?? '',
          }))
        );
        // biome-ignore lint/correctness/noUnusedVariables: migrated from develop
      } catch (e) {
        setHasError(true);
      } finally {
        setFetching(false);
      }
    })();
  }, [compositionFilters, gqlClient]);

  const status = (() => {
    if (fetching) return DataProviderQueryStatus.Fetching;
    if (hasError) return DataProviderQueryStatus.Error;
    return DataProviderQueryStatus.Success;
  })();

  return children({
    data: convertCompositionTopNData({
      data: compositionData,
      isGrouped: item.plot?.chartType !== 'bar',
      metric,
      exchangeRate,
    }),
    status,
  });
};
